/**
 * Agreement versions the owner accepted for their commercial licence.
 *
 * This is intentionally modest: a short history card, not a document browser. The legal
 * text itself still lives on /agreement; this component shows which version the account
 * accepted and when.
 */
import { FileText } from 'lucide-react';
import { c } from '../../styles/theme';
import type { LicenseAgreementHistoryItem } from '../../types/domain';

export interface LicenseAgreementHistoryProps {
  items: LicenseAgreementHistoryItem[];
}

export default function LicenseAgreementHistory({ items }: LicenseAgreementHistoryProps) {
  return (
    <>
      <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>Agreement history</p>
      <div
        style={{
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          background: c.surface1,
          marginBottom: 18,
          overflow: 'hidden',
        }}
      >
        {items.length === 0 ? (
          <div style={{ padding: '14px 16px' }}>
            <p style={{ margin: '0 0 4px', fontSize: 12, color: c.text1 }}>No agreement history yet.</p>
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
              Once the backend records licence acceptance history, the versions you accepted will
              appear here.
            </p>
          </div>
        ) : (
          items.map((item, index) => (
            <div
              key={`${item.version}-${item.acceptedAt}`}
              style={{
                padding: '12px 14px',
                borderBottom: index === items.length - 1 ? undefined : `1px solid ${c.border}`,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <FileText size={14} color={c.accent} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: '0 0 4px', fontSize: 12.5, color: c.text1, fontWeight: 500 }}>
                  {item.title} · v{item.version}
                </p>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: c.text2 }}>
                  Accepted {item.acceptedAt}
                  {item.publishedAt ? ` · Published ${item.publishedAt}` : ''}
                </p>
                {item.documentUrl && (
                  <p style={{ margin: 0, fontSize: 11, color: c.accent, fontFamily: 'IBM Plex Mono' }}>
                    {item.documentUrl}
                  </p>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
